import React, { useState, useMemo } from "react";
import { Confetti, playCorrect, playWrong } from "@/components/Bits";
import { Cell, DoneCard, RoundHeader } from "@/components/Cells";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

/* Tap an item, then tap the bucket it belongs to. rounds: [{title, bins:[{key,label,color?|shape?|emoji?}], items:[{key,label,emoji,bin}]}] */
export default function SortRunner({ title, category, modeId, build, backTo = "/learn" }) {
  const [seed, setSeed] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rounds = useMemo(() => build(seed), [seed, build]);
  const [r, setR] = useState(0);
  const [placed, setPlaced] = useState({});
  const [sel, setSel] = useState(null);
  const [shakeBin, setShakeBin] = useState(null);
  const [done, setDone] = useState(false);
  const round = rounds[r];
  const remaining = round.items.filter(i => !placed[i.key]);

  const drop = (binKey) => {
    if (!sel) return;
    const item = round.items.find(i => i.key === sel);
    const ok = item.bin === binKey;
    recordAnswer(category, ok);
    if (!ok) { playWrong(); setShakeBin(binKey); setTimeout(() => setShakeBin(null), 450); return; }
    playCorrect(item.label);
    const np = { ...placed, [sel]: binKey };
    setPlaced(np); setSel(null);
    if (round.items.every(i => np[i.key])) {
      setTimeout(() => {
        if (r + 1 >= rounds.length) { completeActivity(category, modeId); setDone(true); speak("All sorted! Amazing!"); }
        else { setR(r + 1); setPlaced({}); speak("Next round!"); }
      }, 700);
    }
  };
  const again = () => { setSeed(s => s + 1); setR(0); setPlaced({}); setSel(null); setDone(false); };

  if (done) return <DoneCard title="All sorted!" emoji="🪣" onAgain={again} backTo={backTo} subtitle={`You finished ${title}!`} />;

  return (
    <div className="ll-bg min-h-screen pb-32">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <RoundHeader idx={r} total={rounds.length} label={`Round ${r + 1} / ${rounds.length}`} />
        <div className="ll-card p-5 sm:p-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-purple-900">{round.title}</h2>
          <p className="text-purple-600 font-semibold mt-1" data-testid="sort-hint">{sel ? "Now tap the right bucket!" : "Tap an item, then tap its bucket."}</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-5 min-h-[90px]" data-testid="sort-items">
            {remaining.map(i => (
              <button key={i.key} data-testid={`sort-item-${i.key}`} onClick={() => { setSel(i.key); speak(i.label); }}
                className={`ll-tile bg-white flex items-center justify-center p-2 aspect-square ${sel === i.key ? "ring-4 ring-purple-400 scale-105" : ""}`}>
                <Cell c={i} size="sm" />
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            {round.bins.map(b => (
              <button key={b.key} data-testid={`sort-bin-${b.key}`} onClick={() => drop(b.key)}
                className={`rounded-3xl border-4 border-dashed p-4 min-h-[150px] flex flex-col items-center gap-2 transition ${sel ? "border-purple-400 bg-purple-50" : "border-purple-200 bg-white/60"} ${shakeBin === b.key ? "ll-shake ring-4 ring-pink-300" : ""}`}>
                <Cell c={b} size="sm" />
                <div className="font-display font-bold text-purple-900">{b.label}</div>
                <div className="flex flex-wrap justify-center gap-1">
                  {round.items.filter(i => placed[i.key] === b.key).map(i => <span key={i.key} className="text-2xl ll-pop">{i.emoji}</span>)}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <Confetti show={remaining.length === 0} />
    </div>
  );
}
